import { useMemo } from 'react';
import { useIsMobile } from "react-ui";
import { useMapBoundingBox } from "../contexts/MapBoundingBox";

/**
 * Square edge length (px) used by EventTile for its fixed footprint and by
 * useBottomSheet to compute the half/expanded sheet heights.
 *
 * On mobile the grid is two columns inside `px-[18px] gap-4`, so the
 * available content width is `containerWidth - 2*18 - 16 = containerWidth - 52`.
 * Adding a 1px safety margin (54 total) keeps tiles inside their track even
 * when sub-pixel rounding would otherwise tip them over.
 *
 * IMPORTANT: measure the embed container, not `window.innerWidth`. When the
 * web component is embedded in a host page that sets horizontal padding
 * (e.g. dt-the7 on csr-dialogforum.com), the host viewport is wider than the
 * embed; sizing tiles from the host viewport overflows the grid track,
 * tiles butt up against each other, and the `gap-4` column gap disappears.
 * `useMapBoundingBox()` reports the actual rendered embed width and is
 * already kept in sync via the same resize listener that drives layout.
 */
export const useTileSize = () => {
    const isMobile = useIsMobile();
    const { width } = useMapBoundingBox();
    return useMemo(() => {
        if (!isMobile) return 164;
        // Fallback to window width on the first render before the bounding
        // box context has measured the container, matching previous behaviour.
        const containerWidth = width > 0 ? width : window.innerWidth;
        return (containerWidth - 54) / 2;
    }, [isMobile, width]);
}
